import { __rest, __assign } from 'tslib';
import useForkRef from '@rooks/use-fork-ref';
import * as React from 'react';
import { UitkField } from 'uitk-react-field';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-input.css';

var getClassNameList = classNames.bind(styles);
var ownerDocument = function (node) { return (node && node.ownerDocument) || document; };
var ownerWindow = function (node) {
    var doc = ownerDocument(node);
    return doc.defaultView || window;
};
var getStyleValue = function (computedStyle, property) { return parseInt(computedStyle[property], 10) || 0; };
var shadowStyles = {
    height: 0,
    left: 0,
    minHeight: 'unset',
    // Ignore the scrollbar width
    overflow: 'hidden',
    // Create a new layer, increase the isolation of the computed values
    // Remove from the content flow
    position: 'absolute',
    top: 0,
    transform: 'translateZ(0)',
    // Visibility needed to hide the extra text area on iPads
    visibility: 'hidden'
};
var useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;
var UitkTextArea = React.forwardRef(function (props, ref) {
    var _a;
    var onChange = props.onChange, maxRows = props.maxRows, _b = props.minRows, minRows = _b === void 0 ? 2 : _b, value = props.value, className = props.className, disabled = props.disabled, hasBorder = props.hasBorder, hasFloatedLabel = props.hasFloatedLabel, hasVisibleLabel = props.hasVisibleLabel, id = props.id, instruction = props.instruction, invalid = props.invalid, label = props.label, placeholder = props.placeholder, readOnly = props.readOnly, required = props.required, domRef = props.domRef, other = __rest(props, ["onChange", "maxRows", "minRows", "value", "className", "disabled", "hasBorder", "hasFloatedLabel", "hasVisibleLabel", "id", "instruction", "invalid", "label", "placeholder", "readOnly", "required", "domRef"]);
    var uitkFieldProps = {
        disabled: disabled,
        hasBorder: hasBorder,
        hasFloatedLabel: hasFloatedLabel,
        hasVisibleLabel: hasVisibleLabel,
        id: id,
        instruction: instruction,
        invalid: invalid,
        label: label,
        placeholder: placeholder,
        readOnly: readOnly,
        required: required
    };
    var isControlled = React.useRef(value !== null).current;
    var inputRef = React.useRef(null);
    // Giving priority to domRef as the forwarded ref since it is documented
    // eslint-disable-next-line react-hooks/rules-of-hooks
    var handleRef = domRef ? useForkRef(domRef, inputRef) : useForkRef(ref, inputRef);
    var shadowRef = React.useRef(null);
    var renders = React.useRef(0);
    var _c = React.useState({}), state = _c[0], setState = _c[1];
    var textAreaClassNames = getClassNameList('uitk-field-textarea', className);
    var syncHeight = React.useCallback(function () {
        var input = inputRef.current;
        var inputShallow = shadowRef.current;
        if (input && inputShallow) {
            var containerWindow = ownerWindow(input);
            var computedStyle = containerWindow.getComputedStyle(input);
            inputShallow.value = input.value || placeholder || 'x';
            var boxSizing = computedStyle['box-sizing'];
            var padding = getStyleValue(computedStyle, 'padding-bottom') + getStyleValue(computedStyle, 'padding-top');
            var border = getStyleValue(computedStyle, 'border-bottom-width') + getStyleValue(computedStyle, 'border-top-width');
            // The height of the inner content
            var innerHeight_1 = inputShallow.scrollHeight - padding;
            // Measure height of a textarea with a single row
            inputShallow.value = 'x';
            var singleRowHeight = inputShallow.scrollHeight - padding;
            // The height of the outer content
            var outerHeight_1 = innerHeight_1;
            if (minRows) {
                outerHeight_1 = Math.max(Number(minRows) * singleRowHeight, outerHeight_1);
            }
            if (maxRows) {
                outerHeight_1 = Math.min(Number(maxRows) * singleRowHeight, outerHeight_1);
            }
            outerHeight_1 = Math.max(outerHeight_1, singleRowHeight);
            // Take the box sizing into account for applying this value as a style.
            var outerHeightStyle_1 = outerHeight_1 + (boxSizing === 'border-box' ? padding + border : 0) + singleRowHeight / 2;
            var overflow_1 = Math.abs(outerHeight_1 - innerHeight_1) <= 1;
            setState(function (prevState) {
                // Need a large enough difference to update the height.
                // This prevents infinite rendering loop.
                if (renders.current < 20 &&
                    ((outerHeightStyle_1 > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle_1) > 1) ||
                        prevState.overflow !== overflow_1)) {
                    renders.current += 1;
                    return {
                        outerHeightStyle: outerHeightStyle_1,
                        overflow: overflow_1
                    };
                }
                return prevState;
            });
        }
    }, [maxRows, minRows, placeholder]);
    useEnhancedEffect(function () {
        syncHeight();
    });
    React.useEffect(function () {
        renders.current = 0;
    }, [value]);
    var handleChange = function (event) {
        renders.current = 0;
        if (!isControlled) {
            syncHeight();
        }
        if (onChange) {
            onChange(event);
        }
    };
    return (React.createElement(UitkField, __assign({}, uitkFieldProps),
        React.createElement("textarea", __assign({ "aria-required": required, "aria-invalid": !!invalid, className: getClassNameList(textAreaClassNames, (_a = {}, _a["empty-placeholder"] = !value && !placeholder, _a)), id: id, "aria-describedby": invalid ? "".concat(id, "-error") : undefined, value: value, onChange: handleChange, placeholder: placeholder, ref: handleRef, rows: minRows, style: {
                height: state.outerHeightStyle,
                overflow: state.overflow ? 'hidden' : undefined
            } }, other)),
        React.createElement("textarea", { "aria-hidden": true, className: textAreaClassNames, placeholder: placeholder, readOnly: true, ref: shadowRef, tabIndex: -1, style: __assign({}, shadowStyles), "data-test-id": other['data-test-id'] ? "".concat(other['data-test-id'], "-hidden") : undefined })));
});
UitkTextArea.defaultProps = {
    required: false
};
UitkTextArea.displayName = 'UitkTextArea';

export { UitkTextArea };
